/* ============================================================================
   Admin module — lightweight modal/overlay.
   base.css has no modal primitive, so this composes existing tokens (.card,
   shadows, line colors) into a centered dialog. Closes on Esc / backdrop click.
   Kept inside the module — no shared file is touched.
   ============================================================================ */

import { useEffect, type ReactNode } from 'react';

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}

export default function Modal({ title, onClose, children, footer, width = 520 }: ModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(27, 36, 64, 0.45)',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        zIndex: 1000,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{
          width: '100%',
          maxWidth: width,
          padding: 0,
          boxShadow: 'var(--shadow-lg)',
          maxHeight: 'calc(100vh - 48px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          className="card-header"
          style={{ padding: '18px 22px', margin: 0, borderBottom: '1px solid var(--line)' }}
        >
          <h3>{title}</h3>
          <button
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            aria-label="Close"
            style={{ height: 30, padding: '0 10px' }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: '20px 22px', overflowY: 'auto' }}>{children}</div>

        {footer && (
          <div
            className="row"
            style={{
              padding: '14px 22px',
              borderTop: '1px solid var(--line)',
              justifyContent: 'flex-end',
              gap: 10,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
